// ─────────────────────────────────────────────
//  Facility Settings — Surgeons. The screen that fills the Team page's
//  Surgeons section.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
//
//  ⚠️ NOTHING USED TO CALL addSurgeon(). The `surgeons` collection, its
//  Firestore rules and the Team page's Surgeons section all shipped, and the
//  only writer in the repository was the sandbox seeder — so in production that
//  section could never be anything but empty, and the empty state's own advice
//  ("add them in Setup → Facility") pointed at a screen that did not exist.
//  This panel is that screen. It is modelled on StaffPanel next door because
//  the record is near-identical and a second interaction for the same job is
//  just a second thing to learn.
//
//  ⚠️ WHICH SURGEON LIST IS WHICH — there were THREE, there are now two, and
//  only one of them is edited by a human:
//
//    surgeons/{id}                      AUTHORITATIVE. This panel's collection.
//                                       name · phoneNumber · email · specialty
//                                       · listed · facilityId.
//    procedure_refs/{fid}.surgeons[]    DERIVED. Flat NAMES only — the SURGEON
//                                       typeahead on Daily Assignments. Every
//                                       write below mirrors the name into it
//                                       with addProcedureRef /
//                                       removeProcedureRef, which are arrayUnion
//                                       / arrayRemove: surgical, so the names a
//                                       doctor typed straight into a case row
//                                       are never swept away. CASE ENTRY IS
//                                       UNTOUCHED — the grid still reads the
//                                       same field it always read.
//    procedure_refs/{fid}.surgeonList[] RETIRED. Read once, by the Import
//                                       button below, so a facility's existing
//                                       roster is offered rather than orphaned.
//
//  ⚠️ THE MIRROR MUST NOT FAIL THE WRITE. The typeahead is a convenience; the
//  directory record is the point. If the procedure_refs write is refused the
//  surgeon is still saved and the console says why — the alternative is an
//  admin who cannot add a surgeon because a datalist could not be updated.
//
//  ⚠️ ADMIN ONLY, and that IS a change. firestore.rules gates /surgeons writes
//  on isAdmin(); the retired panel also let an in-facility AIC edit. An AIC can
//  still add a surgeon NAME to the picker by typing it into a case. Said on
//  screen rather than left as a silent permission-denied toast.
//
//  ⚠️ REMOVING A SURGEON IS DESTRUCTIVE AND THE CONFIRMATION NAMES THEM. A
//  dialog that says "Remove this surgeon?" is a dialog nobody reads.
// ─────────────────────────────────────────────
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  listenToSurgeons, addSurgeon, updateSurgeon, removeSurgeon,
  addProcedureRef, removeProcedureRef, getLegacySurgeonRoster,
} from "../firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { useFacility } from "../hooks/useFacility";
import { activeSpecialties } from "../domain/specialties";
import { normalizeSurgeon } from "../domain/surgeons";
import Modal from "../components/Modal";
import { RosterChip, ModalButtons, Field } from "./setupPage.roster";
import tokens from "../theme/tokens";
import s from "./setupPage.styles";

const { color } = tokens;

// An opted-out surgeon is still a working record — it just does not appear on
// the Team page — so it reads in the muted ink, never in an alarm colour.
const UNLISTED_ACCENT = color.text.muted;

const blankForm = { name: "", phoneNumber: "", email: "", specialty: "", listed: true };

// ⚠️ THE SPECIALTY IS STORED AS THE LABEL A HUMAN READS, NOT THE SLUG KEY.
// The Team page prints `surgeon.specialty` verbatim beside the name; storing
// "ortho" would put "ortho" on the directory row. The key belongs to the
// facility's own configurable list, this is a contact record, and the two are
// allowed to be different things.
const specialtyLabels = (opts) => opts.map((sp) => sp.label);

export function SurgeonsPanel({ facilityId }) {
  const { isAdmin } = useAuth();
  const toast = useToast();
  const { facility } = useFacility(facilityId);
  const specialtyOpts = useMemo(() => activeSpecialties(facility?.specialties), [facility]);

  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(blankForm);   // add form
  const [editId, setEditId] = useState(null);    // modal: surgeon id being edited
  const [draft, setDraft] = useState(blankForm); // modal working copy
  const [legacy, setLegacy] = useState([]);      // retired surgeonList rows, if any

  // Live snapshot of the whole collection, filtered here. ONE listener: a
  // facility-scoped query would need its own index and the roster is small.
  useEffect(() => {
    const unsub = listenToSurgeons(
      (snap) => setDocs(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => { console.error("Load surgeons failed:", err); toast.error("Could not load surgeons."); }
    );
    return unsub;
  }, [toast]);

  // Whatever the retired list still holds, so the Import button can offer it.
  // A refusal is not an error worth a toast — the button simply never appears.
  useEffect(() => {
    let live = true;
    getLegacySurgeonRoster(facilityId)
      .then((rows) => { if (live) setLegacy(rows); })
      .catch((err) => console.warn("[surgeons] legacy roster unreadable:", err?.message));
    return () => { live = false; };
  }, [facilityId]);

  // This facility's records — PLUS any that carry no facilityId at all. An
  // unstamped record is tenant-wide (the Team page shows it to everyone, see
  // teamContacts.inFacilityScope), and a panel that hid it would leave a real
  // row on the directory with no screen anywhere that can edit or remove it.
  const surgeons = useMemo(
    () => docs
      .map(normalizeSurgeon)
      .filter((sg) => sg && (!sg.facilityId || sg.facilityId === facilityId))
      .sort((a, b) => a.name.localeCompare(b.name)),
    [docs, facilityId]
  );

  const dupeName = (name, exceptId) =>
    surgeons.some((sg) => sg.id !== exceptId && sg.name.toLowerCase() === name.toLowerCase());

  // Keep the AssignmentGrid's SURGEON typeahead in step. Never fatal — see the
  // header. `from`/`to` both given on a rename so the stale name goes away.
  const mirrorName = async ({ from, to }) => {
    try {
      if (from && from !== to) await removeProcedureRef(facilityId, "surgeons", from);
      if (to) await addProcedureRef(facilityId, "surgeons", to);
    } catch (err) {
      console.warn("[surgeons] could not update the assignment typeahead:", err?.message);
    }
  };

  const add = async () => {
    const name = form.name.trim();
    if (!name) { toast.error("Enter a surgeon name."); return; }
    if (dupeName(name, null)) { toast.error(`"${name}" is already in the list.`); return; }
    setBusy(true);
    try {
      await addSurgeon({
        name,
        phoneNumber: form.phoneNumber.trim(),
        email: form.email.trim().toLowerCase(),
        specialty: form.specialty || specialtyOpts[0]?.label || "",
        listed: form.listed,
        facilityId,
      });
      await mirrorName({ to: name });
      toast.success(`"${name}" added.`);
      setForm(blankForm);
    } catch (err) {
      console.error("Add surgeon failed:", err);
      toast.error("Could not add the surgeon. Admin rights are required.");
    } finally { setBusy(false); }
  };

  const openEdit = (sg) => {
    setEditId(sg.id);
    setDraft({
      name: sg.name, phoneNumber: sg.phone, email: sg.email,
      specialty: sg.specialty || "", listed: sg.listed,
    });
  };
  // ⚠️ useCallback IS LOAD-BEARING, NOT TIDINESS. Modal's focus-trap effect
  // lists `onClose` in its dependency array. A fresh arrow per render makes that
  // effect re-run on EVERY keystroke, and its first act is to focus the panel's
  // first focusable element — so the second character typed into Email lands in
  // Name. A stable identity is what keeps the modal typeable.
  const closeEdit = useCallback(() => setEditId(null), []);

  const saveEdit = async () => {
    const name = draft.name.trim();
    if (!name) { toast.error("Enter a surgeon name."); return; }
    if (dupeName(name, editId)) { toast.error(`"${name}" is already in the list.`); return; }
    const before = surgeons.find((sg) => sg.id === editId);
    setBusy(true);
    try {
      await updateSurgeon(editId, {
        name,
        phoneNumber: draft.phoneNumber.trim(),
        email: draft.email.trim().toLowerCase(),
        specialty: draft.specialty || "",
        listed: draft.listed,
      });
      await mirrorName({ from: before?.name, to: name });
      toast.success(`"${name}" updated.`);
      closeEdit();
    } catch (err) {
      console.error("Save surgeon failed:", err);
      toast.error("Could not save the surgeon. Admin rights are required.");
    } finally { setBusy(false); }
  };

  // ⚠️ NAMES THE PERSON, and says what else goes with them. Cases already
  // saved keep the surgeon's name — the grid shows a stored value that is no
  // longer in the picker as-is rather than dropping it.
  const remove = async (sg) => {
    const ok = window.confirm(
      `Remove ${sg.name} from the surgeon directory?\n\n`
      + "Their phone number and email are deleted and they stop appearing on the Team page. "
      + "Cases already assigned to them keep their name."
    );
    if (!ok) return;
    setBusy(true);
    try {
      await removeSurgeon(sg.id);
      await mirrorName({ from: sg.name });
      toast.info(`${sg.name} removed.`);
    } catch (err) {
      console.error("Remove surgeon failed:", err);
      toast.error("Could not remove the surgeon. Admin rights are required.");
    } finally { setBusy(false); }
  };

  // The retired list, minus everyone already carried across. Idempotent, same
  // shape as StaffPanel's "Import existing providers".
  const pendingLegacy = useMemo(() => {
    const have = new Set(surgeons.map((sg) => sg.name.toLowerCase()));
    const seen = new Set();
    return legacy.filter((row) => {
      const key = String(row.name || "").trim().toLowerCase();
      if (!key || have.has(key) || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [legacy, surgeons]);

  const importLegacy = async () => {
    setBusy(true);
    let added = 0;
    try {
      for (const row of pendingLegacy) {
        const name = String(row.name).trim();
        await addSurgeon({
          name,
          phoneNumber: String(row.phone || "").trim(),
          // The retired rows stored a specialty KEY. Resolve it to the label
          // this collection stores; an unknown key becomes no specialty rather
          // than a slug printed on the directory row.
          specialty: specialtyOpts.find((sp) => sp.key === row.specialty)?.label || "",
          listed: true,
          facilityId,
        });
        await mirrorName({ to: name });
        added += 1;
      }
      toast.success(`Imported ${added} surgeon${added === 1 ? "" : "s"} from the old list.`);
    } catch (err) {
      console.error("Import surgeons failed:", err);
      toast.error(`Imported ${added} before failing. Admin rights are required.`);
    } finally { setBusy(false); }
  };

  // Boxes by specialty, in the facility's configured order, with anything
  // unrecognised gathered under one heading rather than dropped.
  const groups = useMemo(() => {
    const byLabel = new Map(specialtyLabels(specialtyOpts).map((label) => [label, []]));
    for (const sg of surgeons) {
      const label = byLabel.has(sg.specialty) ? sg.specialty : "Unspecified";
      if (!byLabel.has(label)) byLabel.set(label, []);
      byLabel.get(label).push(sg);
    }
    return [...byLabel.entries()]
      .filter(([, list]) => list.length > 0)
      .map(([label, list]) => ({ label, list }));
  }, [surgeons, specialtyOpts]);

  return (
    <div style={s.panel}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <div style={{ ...s.panelTitle, marginBottom: 0 }}>🩺 Surgeons</div>
        {isAdmin && pendingLegacy.length > 0 && (
          <button style={s.outlineBtn} onClick={importLegacy} disabled={busy}
            title="Copy the surgeons this facility kept in the old list into the directory">
            ⬇ Import {pendingLegacy.length} from the old list
          </button>
        )}
      </div>
      <p style={{ fontSize: 12, color: color.text.muted, marginTop: 0, marginBottom: 14 }}>
        Facility-specific. These are the surgeons the Team page lists, and their names
        also feed the SURGEON dropdown on Daily Assignments. Hover a name for phone + edit.
        {!isAdmin && " Only an administrator can edit the surgeon directory."}
      </p>

      {isAdmin && (
        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ flex: "1 1 170px", minWidth: 150 }}>
            <label style={s.label} htmlFor="surgeon-name">Name</label>
            <input id="surgeon-name" style={s.input} placeholder="Surgeon name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              onKeyDown={(e) => { if (e.key === "Enter") add(); }} disabled={busy} />
          </div>
          <div style={{ width: 150 }}>
            <label style={s.label} htmlFor="surgeon-phone">Phone</label>
            <input id="surgeon-phone" style={s.input} type="tel" placeholder="(813) 555-0123" value={form.phoneNumber}
              onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })}
              onKeyDown={(e) => { if (e.key === "Enter") add(); }} disabled={busy} />
          </div>
          <div style={{ width: 190 }}>
            <label style={s.label} htmlFor="surgeon-email">Email</label>
            <input id="surgeon-email" style={s.input} type="email" placeholder="name@example.org" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              onKeyDown={(e) => { if (e.key === "Enter") add(); }} disabled={busy} />
          </div>
          <div style={{ width: 170 }}>
            <label style={s.label} htmlFor="surgeon-specialty">Specialty</label>
            <select id="surgeon-specialty" style={s.select} value={form.specialty || specialtyOpts[0]?.label || ""}
              onChange={(e) => setForm({ ...form, specialty: e.target.value })} disabled={busy}>
              {specialtyOpts.map((sp) => <option key={sp.key} value={sp.label}>{sp.label}</option>)}
            </select>
          </div>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: color.text.primary, paddingBottom: 9 }}>
            <input type="checkbox" checked={form.listed} disabled={busy}
              onChange={(e) => setForm({ ...form, listed: e.target.checked })} />
            Show on Team page
          </label>
          <button style={s.primaryBtn} onClick={add} disabled={busy || !form.name.trim()}>＋ Add Surgeon</button>
        </div>
      )}

      {groups.length === 0 && <div style={{ fontSize: 12, color: color.text.muted }}>No surgeons yet.</div>}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 }}>
        {groups.map((g) => (
          <div key={g.label} style={{ background: color.surface.raised, border: `1px solid ${color.border.default}`, borderRadius: 8, padding: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".5px", color: color.text.muted, marginBottom: 10 }}>
              {g.label} · {g.list.length}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {g.list.map((sg) => (
                <RosterChip key={sg.id} name={sg.name}
                  phone={`${sg.phoneDisplay || "No phone"}${sg.listed ? "" : " · hidden from Team"}`}
                  accent={sg.listed ? color.accent.primary : UNLISTED_ACCENT}
                  canEdit={isAdmin} busy={busy}
                  onEdit={() => openEdit(sg)} onRemove={() => remove(sg)} />
              ))}
            </div>
          </div>
        ))}
      </div>

      <Modal isOpen={editId != null} onClose={closeEdit} title="Edit Surgeon"
        footer={<ModalButtons onSave={saveEdit} onCancel={closeEdit} busy={busy} />}>
        <Field label="Name">
          <input style={s.input} value={draft.name} autoFocus
            onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
        </Field>
        <Field label="Phone">
          <input style={s.input} type="tel" value={draft.phoneNumber}
            onChange={(e) => setDraft({ ...draft, phoneNumber: e.target.value })} />
        </Field>
        <Field label="Email">
          <input style={s.input} type="email" value={draft.email}
            onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
        </Field>
        <Field label="Specialty">
          <select style={s.select} value={draft.specialty || ""}
            onChange={(e) => setDraft({ ...draft, specialty: e.target.value })}>
            <option value="">Unspecified</option>
            {specialtyOpts.map((sp) => <option key={sp.key} value={sp.label}>{sp.label}</option>)}
          </select>
        </Field>
        <Field label="Team page">
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, color: color.text.primary }}>
            <input type="checkbox" checked={draft.listed}
              onChange={(e) => setDraft({ ...draft, listed: e.target.checked })} />
            Show this surgeon on the Team page
          </label>
        </Field>
      </Modal>
    </div>
  );
}

export default SurgeonsPanel;
